/**
 * Composants UI partagés du back-office Stella Cars.
 * Tous orientés "premium dashboard" : dark, accents or, micro-typo.
 */
import { useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import type { LeadStatut } from '../lib/api';

const EUR = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

// ── Compteur animé (réutilisé partout) ──────────────────────────────────────
export function AnimatedNumber({
  value,
  duration = 1.4,
  className,
}: {
  value: number;
  duration?: number;
  className?: string;
}) {
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => Math.round(v).toLocaleString('fr-FR'));
  useEffect(() => {
    const ctrl = animate(mv, value, { duration, ease: [0.22, 1, 0.36, 1] });
    return () => ctrl.stop();
  }, [value, duration, mv]);
  return <motion.span className={className}>{display}</motion.span>;
}

// ── Métrique monétaire élégante ─────────────────────────────────────────────
export function MoneyMetric({
  label,
  value,
  tone = 'neutral',
  size = 'md',
}: {
  label: string;
  value: number;
  tone?: 'gold' | 'success' | 'warning' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
}) {
  const tones = {
    gold: 'text-accent-gold',
    success: 'text-success',
    warning: 'text-warning',
    neutral: 'text-cream',
  };
  const sizes = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-4xl',
  };
  return (
    <div className="rounded-xl border border-white/5 bg-primary-bg/40 p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-dark">{label}</div>
      <div className={`mt-2 font-display font-semibold ${sizes[size]} ${tones[tone]}`}>
        <AnimatedNumber value={value} /> €
      </div>
    </div>
  );
}

// ── Badge statut lead ────────────────────────────────────────────────────────
const STATUT_STYLES: Record<LeadStatut, { bg: string; fg: string; label: string }> = {
  nouveau: { bg: 'bg-info/15', fg: 'text-info', label: 'Nouveau' },
  qualifié: { bg: 'bg-accent-gold/15', fg: 'text-accent-gold', label: 'Qualifié' },
  rdv_pris: { bg: 'bg-success/15', fg: 'text-success', label: 'RDV pris' },
  relance: { bg: 'bg-warning/15', fg: 'text-warning', label: 'Relance' },
  relance_1: { bg: 'bg-warning/15', fg: 'text-warning', label: 'Relance 1' },
  relance_2: { bg: 'bg-warning/25', fg: 'text-warning', label: 'Relance 2' },
  signe: { bg: 'bg-success/25', fg: 'text-success', label: 'Signé' },
  gagné: { bg: 'bg-success/25', fg: 'text-success', label: 'Gagné' },
  perdu: { bg: 'bg-error/15', fg: 'text-error', label: 'Perdu' },
  archivé: { bg: 'bg-muted-dark/20', fg: 'text-muted-dark', label: 'Archivé' },
};

export function StatusBadge({ statut }: { statut: LeadStatut }) {
  const s = STATUT_STYLES[statut] ?? STATUT_STYLES.nouveau;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${s.bg} ${s.fg}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {s.label}
    </span>
  );
}

// ── Toggle de vue (Jour / Semaine / Mois) ──────────────────────────────────
export type CalendarView = 'jour' | 'semaine' | 'mois';

export function ViewToggle({
  value,
  onChange,
}: {
  value: CalendarView;
  onChange: (v: CalendarView) => void;
}) {
  const opts: { id: CalendarView; label: string }[] = [
    { id: 'jour', label: 'Jour' },
    { id: 'semaine', label: 'Semaine' },
    { id: 'mois', label: 'Mois' },
  ];
  return (
    <div className="inline-flex rounded-full border border-white/10 bg-primary-bg/60 p-1">
      {opts.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={`relative rounded-full px-4 py-1.5 text-xs font-medium transition ${
              active ? 'text-primary-bg' : 'text-cream/70 hover:text-cream'
            }`}
          >
            {active && (
              <motion.span
                layoutId="view-toggle-pill"
                className="absolute inset-0 rounded-full bg-gradient-to-r from-accent-gold to-accent-goldLight"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              />
            )}
            <span className="relative">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Formatter Euro réexporté ────────────────────────────────────────────────
export function eur(v: number): string {
  return EUR.format(v);
}

// ── Loading primitives ──────────────────────────────────────────────────────

/** Anneau spinner doré. */
export function Spinner({ size = 18, className = '' }: { size?: number; className?: string }) {
  return (
    <span
      role="status"
      aria-label="Chargement"
      className={`inline-block ${className}`}
      style={{ width: size, height: size }}
    >
      <span className="spinner-gold block" />
    </span>
  );
}

/** Trois points dorés qui pulsent — pour inline / boutons. */
export function LoadingDots({ className = '' }: { className?: string }) {
  return (
    <span className={`dot-pulse inline-flex items-center gap-1 ${className}`} aria-label="Chargement">
      <span />
      <span />
      <span />
    </span>
  );
}

/** Bloc shimmer (squelette). */
export function Skeleton({
  className = '',
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return <div className={`skeleton ${className}`} style={style} aria-hidden="true" />;
}

/** Squelette d'une carte KPI. */
export function KpiCardSkeleton({ index = 0 }: { index?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.04 * index }}
      className="relative overflow-hidden rounded-2xl border border-white/5 bg-primary-surface/70 p-5"
    >
      <div className="flex items-start justify-between">
        <Skeleton className="h-9 w-9 rounded-lg" />
        <Skeleton className="h-3 w-24" />
      </div>
      <Skeleton className="mt-5 h-8 w-28" />
      <Skeleton className="mt-2 h-3 w-32" />
    </motion.div>
  );
}

/** Squelette d'une ligne de tableau. */
export function RowSkeleton({ cols = 5 }: { cols?: number }) {
  return (
    <tr className="border-b border-white/5 last:border-0">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-4">
          <Skeleton className={`h-3 ${i === 0 ? 'w-40' : i === cols - 1 ? 'w-20' : 'w-28'}`} />
        </td>
      ))}
    </tr>
  );
}

/** Loader plein écran (logo + dots) — pour Suspense ou boot. */
export function FullPageLoader({ label = 'Chargement…' }: { label?: string }) {
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-5 bg-primary-bg">
      <div className="relative h-16 w-16">
        <span className="halo-pulse absolute inset-0 rounded-full bg-accent-gold/20 blur-xl" />
        <Spinner size={64} className="relative" />
      </div>
      <div className="flex flex-col items-center gap-2">
        <span className="font-display text-xs uppercase tracking-[0.32em] text-muted-dark">
          {label}
        </span>
        <LoadingDots />
      </div>
    </div>
  );
}

/**
 * Barre de progression dorée fixée en haut de l'écran.
 * S'affiche dès qu'une query ou une mutation TanStack est en cours
 * (couvre TOUS les rafraîchissements de données du back-office).
 */
export function TopProgressBar() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const active = fetching + mutating > 0;
  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="topbar"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="pointer-events-none fixed inset-x-0 top-0 z-[60]"
        >
          <div className="topbar-progress" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
